import { PluginService } from '../../services/plugin.ts';
import { RuleService, Rule } from '../../services/rule.ts';
import { DomainService, Domain } from '../../services/domain.ts';
import { ToastService } from '../../services/toast.ts';
import { Plugin } from '../../models/types.ts';
// @ts-ignore
import jsonata from 'jsonata';
// @ts-ignore
import * as yaml from 'js-yaml';

import '../library/ui/app-button.ts';
import '../library/ui/app-icon.ts';
import '../library/ui/app-badge.ts';
import '../library/ui/app-card.ts';
import '../library/form/form-input.ts';
import '../library/form/form-select.ts';
import '../library/form/form-toggle.ts';
import { getPluginEditorStyles } from './plugin-editor-styles.ts';
import { PluginConfigManager } from './plugin-config-manager.ts';

export class PluginEditor extends HTMLElement {
    private pluginService = PluginService.getInstance();
    private ruleService = RuleService.getInstance();
    private domainService = DomainService.getInstance();
    private toastService = ToastService.getInstance();

    private _orgId: string = '';
    private _pluginId: string | null = null;
    private _plugin: Plugin | null = null;
    
    private isLoading = false;
    private isSaving = false;
    private seed = Math.floor(Math.random() * 10000);

    // Form State
    private pendingName: string = '';
    private pendingType: string = 'transform';
    private pendingSide: 'inbound' | 'outbound' = 'inbound';
    private pendingActive: boolean = true;
    private currentConfig: string = '';

    // Editor/Simulation State
    private sampleSource: string = '';
    private transformPreview: string = '';
    private fullTransformedPayload: import('../../models/types.ts').PluginSimulationPayload | null = null;
    private sourceType: 'custom' | 'domain' = 'custom';
    private selectedDomainId: string = '';
    private selectedRuleId: string = '';
    private sourceRules: Rule[] = [];
    private sampleSourceMethod: string = 'GET';
    private sampleSourcePath: string = '/';


    private selectedDestination: string = '';
    private manualContext: string = '';
    private allDomains: Domain[] = [];
    private allRules: Rule[] = [];
    private rulePlugins: Plugin[] = [];
    
    // Layout State
    private colWeights = [30, 35, 35]; // Percentages for 3 columns
    private isResizing = false;
    private resizeIndex = -1;
    private isMockingMode: boolean = false;
    private isTransformPlugin = true;

    // Simulation Execution State
    private simLoading = false;
    private simResult: import('../../models/types.ts').PluginSimulationResult | null = null;
    private simError: string | null = null;
    private activeTab: 'preview' | 'response' = 'preview';

    // Bound handlers for listener management
    private _boundMouseMove = this.handleGlobalMouseMove.bind(this);
    private _boundMouseUp = this.handleGlobalMouseUp.bind(this);

    private get sampleLabel(): string {
        return this.pendingSide === 'inbound' ? 'Sample Request' : 'Sample Response';
    }

    private get previewLabel(): string {
        return this.pendingSide === 'inbound' ? 'Transformed Request' : 'Transformed Response';
    }

    constructor() {
        super();
        this.attachShadow({ mode: 'open' });
    }

    static get observedAttributes() {
        return ['org-id', 'plugin-id'];
    }

    attributeChangedCallback(name: string, oldValue: string, newValue: string) {
        if (oldValue === newValue) return;
        if (name === 'org-id') this._orgId = newValue;
        if (name === 'plugin-id') this._pluginId = newValue;
        
        if (this._orgId) {
            this.init();
        }
    }

    connectedCallback() {
        document.body.classList.add('plugin-editor-open');
        this.render();
        window.addEventListener('mousemove', this._boundMouseMove);
        window.addEventListener('mouseup', this._boundMouseUp);
    }

    disconnectedCallback() {
        document.body.classList.remove('plugin-editor-open');
        window.removeEventListener('mousemove', this._boundMouseMove);
        window.removeEventListener('mouseup', this._boundMouseUp);
    }

    private async init() {
        this.isLoading = true;
        this.render();

        try {
            await Promise.all([
                this.loadPlugin(),
                this.loadSimulationData()
            ]);
            
            this.manualContext = this.getGatewayContext();
            this.updatePreview();
        } catch (e: unknown) {
            const error = e as Error;
            this.toastService.error(error.message || 'Failed to initialize editor');
        } finally {
            this.isLoading = false;
            this.render();
        }
    }

    private async loadPlugin() {
        if (!this._pluginId) {
            this._plugin = null;
            this.pendingName = '';
            this.pendingType = 'transform';
            this.pendingSide = 'inbound';
            this.pendingActive = true;
            this.currentConfig = '$'; // Default pass-through mapping
            this.sampleSource = this.getDefaultSampleSource('transform');
            this.activeTab = 'preview';
            return;
        }

        try {
            const plugins = await this.pluginService.listPlugins(this._orgId);
            const plugin = plugins.find(p => p.id === this._pluginId);
            if (plugin) {
                this._plugin = plugin;
                this.pendingName = plugin.name;
                this.pendingType = plugin.type;
                this.pendingSide = plugin.side;
                this.pendingActive = plugin.active;

                const isTransform = ['transform', 'request_transform', 'response_transform'].includes(plugin.type);
                this.activeTab = isTransform ? 'preview' : 'response';

                if (typeof plugin.config === 'object') {
                    if (plugin.type === 'transform') {

                        this.currentConfig = plugin.config.expression || '';
                    } else {
                        this.currentConfig = JSON.stringify(plugin.config, null, 2);
                    }
                } else {
                    this.currentConfig = plugin.config || '';
                }
                this.sampleSource = this.getDefaultSampleSource(plugin.type, plugin.side);
            }
        } catch (e) {
            console.error('Failed to load plugin', e);
        }
    }

    private async loadSimulationData() {
        if (!this._orgId) return;
        try {
            const domains = await this.domainService.listByOrganization(this._orgId);
            this.allDomains = domains || [];

            const rulePromises = this.allDomains.map(d => this.ruleService.listByDomain(d.id!));
            const rulesResults = await Promise.all(rulePromises);
            this.allRules = rulesResults.flat() || [];

            // Auto-select if not selected
            if (this.allDomains.length > 0 && !this.selectedDomainId) {
                this.selectedDomainId = this.allDomains[0].id!;
                this.sourceRules = this.allRules.filter(r => r.domain_id === this.selectedDomainId);
                
                if (this.sourceRules.length > 0 && !this.selectedRuleId) {
                    this.selectedRuleId = this.sourceRules[0].id!;
                    const rule = this.sourceRules[0];
                    this.sampleSourceMethod = rule.methods?.[0] || 'GET';
                    this.sampleSourcePath = rule.path || '/';
                }
            }
            this.syncSampleSourceFromForm();
        } catch (e) {
            console.error('Failed to load simulation data', e);
        }
    }

    private getDefaultSourceType(type: string): 'custom' | 'domain' {
        if (type === 'transform') {

            return 'custom';
        }
        return 'custom';
    }

    private getDefaultSampleSource(type: string, side: string = 'inbound'): string {
        const commonHeaders = {
            "Content-Type": "application/json",
            "Authorization": "Bearer sample_token"
        };

        if (side === 'outbound' || type === 'response_transform') {
            return JSON.stringify({
                status: 200,
                headers: {
                    "Content-Type": "application/json",
                    "X-Powered-By": "Crate-Gateway",
                    "Cache-Control": "no-cache"
                },
                body: {
                    "id": "res_999",
                    "status": "success",
                    "data": {
                        "items": [1, 2, 3],
                        "total": 3
                    }
                }
            }, null, 2);
        }

        if (type === 'api_key_validation') {
            return JSON.stringify({
                method: "GET",
                url: "https://api.example.com/data",
                headers: {
                    ...commonHeaders,
                    "X-API-Key": "sample-key-123"
                },
                body: {}
            }, null, 2);
        }

        return JSON.stringify({
            method: "POST",
            url: "https://api.example.com/v1/users",
            headers: commonHeaders,
            body: {
                "name": "John Doe",
                "email": "john@example.com"
            }
        }, null, 2);
    }

    private getGatewayContext(): string {
        return "{}"; // Context variables not yet supported by backend
    }

    private handleGlobalMouseMove(e: MouseEvent) {
        if (!this.isResizing) return;

        const grid = this.shadowRoot?.querySelector('.mapping-grid') as HTMLElement;
        if (!grid) return;

        const rect = grid.getBoundingClientRect();
        const mouseX = e.clientX - rect.left;
        const totalWidth = rect.width;
        const mousePercent = (mouseX / totalWidth) * 100;

        if (this.resizeIndex === 0) {
            const diff = mousePercent - this.colWeights[0];
            if (this.colWeights[0] + diff > 10 && this.colWeights[1] - diff > 10) {
                this.colWeights[0] += diff;
                this.colWeights[1] -= diff;
                this.updateColumnWidths();
            }
        } else if (this.resizeIndex === 1) {
            const currentRightOfCol1 = this.colWeights[0] + this.colWeights[1];
            const diff = mousePercent - currentRightOfCol1;
            if (this.colWeights[1] + diff > 10 && this.colWeights[2] - diff > 10) {
                this.colWeights[1] += diff;
                this.colWeights[2] -= diff;
                this.updateColumnWidths();
            }
        }
    }

    private handleGlobalMouseUp() {
        if (this.isResizing) {
            this.isResizing = false;
            this.resizeIndex = -1;
            document.body.style.cursor = 'default';
        }
    }

    private updateColumnWidths() {
        const grid = this.shadowRoot?.querySelector('.mapping-grid') as HTMLElement;
        if (grid) {
            grid.style.gridTemplateColumns = `${this.colWeights[0]}fr 4px ${this.colWeights[1]}fr 4px ${this.colWeights[2]}fr`;
        }
    }

    private async updatePreview() {
        if (!['transform', 'request_transform', 'response_transform'].includes(this.pendingType)) {
            this.transformPreview = "Preview not available for this plugin type.";
            this.renderColumn(2);
            return;
        }

        try {
            const source = JSON.parse(this.sampleSource);
            const expression = this.currentConfig;

            if (!expression) {
                this.transformPreview = "// Type your JSONata logic in the middle pane";
                this.renderColumn(2);
                return;
            }

            const domain = this.allDomains.find(d => d.id === this.selectedDomainId);
            const host = domain ? domain.host : 'api.example.com';

            const requestInput = { 
                method: this.sampleSourceMethod,
                url: {
                    path: this.sampleSourcePath,
                    host: host,
                    query: source.url?.split('?')[1] ? Object.fromEntries(new URLSearchParams(source.url.split('?')[1])) : {}
                },
                headers: source.headers || {},
                body: source.body || {},
                params: {},
                target: {
                    host: 'internal-service',
                    path: this.sampleSourcePath,
                    raw: `http://internal-service${this.sampleSourcePath}`
                }
            };

            let input: any = requestInput;
            const isOutbound = this.pendingSide === 'outbound' || this.pendingType === 'response_transform';

            if (isOutbound) {
                // For response transforms, the top level context is the response
                // If the source has status/headers/body, use it as the response context
                if (source.status !== undefined || source.headers || source.body) {
                    input = {
                        status: source.status || 200,
                        headers: source.headers || { 'Content-Type': 'application/json' },
                        body: source.body || {},
                        request: requestInput,
                        params: requestInput.params,
                        url: requestInput.url,
                        target: requestInput.target
                    };
                } else {
                    // Otherwise treat the whole source as the body
                    input = {
                        status: 200,
                        headers: { 'Content-Type': 'application/json' },
                        body: source,
                        request: requestInput,
                        params: requestInput.params,
                        url: requestInput.url,
                        target: requestInput.target
                    };
                }
            }

            const engine = jsonata(expression);
            const result = await engine.evaluate(input);
            
            this.transformPreview = JSON.stringify(result, null, 2);
            this.fullTransformedPayload = result;
        } catch (e) {
            this.transformPreview = `// Error: ${(e as Error).message}`;
            this.fullTransformedPayload = null;
        }
        
        this.renderColumn(2);
    }

    private async handleRunSimulation() {
        if (this.simLoading) return;

        try {
            this.simLoading = true;
            this.simError = null;
            this.simResult = null;
            this.activeTab = 'response'; // Switch to response tab to show results
            this.renderColumn(2); // Re-render column 3 to show loading state

            const sourceEditor = this.shadowRoot?.querySelector('#source-editor') as HTMLElement;
            if (sourceEditor) this.sampleSource = sourceEditor.innerText;

            const logicEditor = this.shadowRoot?.querySelector('#logic-editor') as HTMLElement;
            let logicContent = '';
            let configObj: Record<string, any> = {};

            if (this.isTransformPlugin) {
                logicContent = logicEditor ? logicEditor.innerText : '';
                configObj = { expression: logicContent };
            } else {
                configObj = PluginConfigManager.getConfigFromForm(this.shadowRoot!, this.pendingType);
            }

            const currentPlugin = {
                type: this.pendingType,
                config: configObj,
                logic: this.isTransformPlugin ? logicContent : ''
            };

            const pluginChain: any[] = [];
            
            if (this.pendingSide === 'outbound') {
                // For outbound testing, we use the "Sample Response" (sampleSource)
                // as the output of a mock plugin that runs before our transform.
                let mockBody = this.sampleSource;
                try {
                    const sourceObj = JSON.parse(this.sampleSource);
                    mockBody = sourceObj.body || sourceObj;
                } catch (e) {
                    // Not JSON or missing body, use raw source
                }

                pluginChain.push({
                    type: 'mock',
                    config: { 
                        status: 200, 
                        body: mockBody,
                        headers: { "Content-Type": "application/json" }
                    },
                    logic: ''
                });
                
                pluginChain.push(currentPlugin);
            } else {
                // Inbound mode
                pluginChain.push(currentPlugin);

                if (this.pendingType !== 'mock') {
                    pluginChain.push({
                        type: 'mock',
                        config: { 
                            status: 200, 
                            body: { message: "Simulation success", transformed: true },
                            headers: { "Content-Type": "application/json", "X-Simulation": "true" }
                        },
                        logic: ''
                    });
                }
            }

            let payload: import('../../models/types.ts').PluginSimulationPayload;

            // For all plugins, we simulate with the original sample source
            // The backend handles applying the plugin chain, including transforms.
            try {
                const methodInput = this.shadowRoot?.querySelector('#mock-method') as HTMLSelectElement;
                const pathInput = this.shadowRoot?.querySelector('#mock-path') as HTMLInputElement;
                const method = methodInput?.value || this.sampleSourceMethod;
                const path = pathInput?.value || this.sampleSourcePath;

                const domain = this.allDomains.find(d => d.id === this.selectedDomainId);
                const host = domain ? domain.host : 'api.example.com';

                const source = JSON.parse(this.sampleSource);
                payload = {
                    method: method,
                    url: `https://${host}${path.startsWith('/') ? '' : '/'}${path}`,
                    headers: source.headers || {},
                    body: typeof source.body === 'object' ? JSON.stringify(source.body) : (source.body || ''),
                    plugins: pluginChain,
                    context: {
                        domain_id: this.selectedDomainId || null,
                        rule_id: this.selectedRuleId || null,
                        position: this.pendingSide
                    }
                };
            } catch (e) {
                throw new Error('Invalid JSON in Input Payload');
            }

            const result = await this.pluginService.simulatePlugin(payload);
            this.simResult = result;
        } catch (e: unknown) {
            const error = e as Error;
            this.simError = error.message || 'Simulation failed';
        } finally {
            this.simLoading = false;
            this.renderColumn(2);
        }
    }

    private async handleSave() {
        if (this.isSaving) return;
        
        if (!this.pendingName) {
            this.toastService.warning('Name is required');
            return;
        }

        this.isSaving = true;
        this.render();

        try {
            let config;
            if (this.isTransformPlugin) {
                config = { expression: this.currentConfig };
            } else {
                config = PluginConfigManager.getConfigFromForm(this.shadowRoot!, this.pendingType);
            }

            const pluginData: Plugin = {
                organization_id: this._orgId,
                name: this.pendingName,
                type: this.pendingType,
                side: this.pendingSide,
                active: this.pendingActive,
                config
            };

            if (this._pluginId) {
                pluginData.id = this._pluginId;
                await this.pluginService.updatePlugin(pluginData);
                this.toastService.success('Plugin updated');
            } else {
                const created = await this.pluginService.createPlugin(pluginData);
                this._pluginId = created.id!;
                this.toastService.success('Plugin created');
            }

            this.dispatchEvent(new CustomEvent('save', { detail: pluginData }));
            this.init(); // Refresh to ensure state is clean
        } catch (e: unknown) {
            const error = e as Error;
            this.toastService.error(error.message || 'Failed to save plugin');
        } finally {
            this.isSaving = false;
            this.render();
        }
    }

    private handleClose() {
        this.dispatchEvent(new CustomEvent('close'));
    }

    private renderColumn(index: number) {
        const grid = this.shadowRoot?.querySelector('.mapping-grid') as HTMLElement;
        const col = grid?.querySelectorAll('.mapping-col')[index] as HTMLElement;
        if (!col) return;

        const isTransform = ['transform', 'request_transform', 'response_transform'].includes(this.pendingType);
        this.isTransformPlugin = isTransform;

        this.updateGridLayout();

        if (index === 0) {
            // Stacked Column: Context Selector + Request Editor + Context Variables
            const domain = this.allDomains.find(d => d.id === this.selectedDomainId);
            const host = domain ? domain.host : 'api.example.com';

            const contextVars = [
                { key: 'method', val: this.sampleSourceMethod },
                { key: 'url.path', val: this.sampleSourcePath },
                { key: 'url.host', val: host },
                { key: 'url.query', val: '{}' },
                { key: 'headers', val: '{...}' },
                { key: 'body', val: '{...}' },
                { key: 'params', val: '{}' },
                { key: 'target.host', val: 'internal-service' },
                { key: 'target.path', val: this.sampleSourcePath }
            ];

            col.innerHTML = `
                <div class="col-row-top" style="flex: 2;">
                    <div class="section-tag tag-request">
                        <app-icon name="arrow-right" size="xs"></app-icon> ${this.sampleLabel}
                    </div>
                    <div class="editor-container" style="flex: 1; min-height: 200px; margin-top: 0.5rem;">
                        <div class="line-numbers" id="source-lines"></div>
                        <div class="editor-content editable" id="source-editor" contenteditable="true" spellcheck="false"></div>
                    </div>
                </div>

                <div class="col-row-bottom" style="flex: 1;">
                    <div class="section-tag tag-vars">
                        <app-icon name="info" size="xs"></app-icon> Context Variables
                    </div>
                    <div class="context-list">
                        ${contextVars.map(v => `
                            <div class="context-item" data-tooltip="Click to copy path">
                                <span class="context-key">${v.key}</span>
                                <span class="context-val">${v.val}</span>
                            </div>
                        `).join('')}
                    </div>
                </div>
            `;

            // Bind Column 0 Events (some moved to bindUIEvents)
            const editor = col.querySelector('#source-editor') as HTMLElement;
            if (editor) {
                editor.innerText = this.sampleSource;
                this.updateLineNumbers(editor, col.querySelector('#source-lines') as HTMLElement);
                editor.oninput = () => {
                    this.sampleSource = editor.innerText;
                    this.updateLineNumbers(editor, col.querySelector('#source-lines') as HTMLElement);
                    this.updatePreview();
                };
            }

            col.querySelectorAll('.context-item').forEach(item => {
                item.addEventListener('click', () => {
                    const key = item.querySelector('.context-key')?.textContent;
                    if (key) {
                        navigator.clipboard.writeText(key);
                        this.toastService.success(`Copied ${key} to clipboard`);
                    }
                });
            });

        } else if (index === 1) {
            // Logic / Config
            if (isTransform) {
                col.innerHTML = `
                    <div class="col-content-padded" style="flex: 1; display: flex; flex-direction: column;">
                        <div class="section-tag tag-logic">
                            <app-icon name="code" size="xs"></app-icon> Logic Engine
                        </div>
                        <div class="editor-container" style="flex: 1; margin-top: 0.5rem;">
                            <div class="line-numbers" id="logic-lines"></div>
                            <div class="editor-content editable" id="logic-editor" contenteditable="true" spellcheck="false"></div>
                        </div>
                    </div>
                `;
                const editor = col.querySelector('#logic-editor') as HTMLElement;
                if (editor) {
                    editor.innerText = this.currentConfig;
                    this.updateLineNumbers(editor, col.querySelector('#logic-lines') as HTMLElement);
                    editor.oninput = () => {
                        this.currentConfig = editor.innerText;
                        this.updateLineNumbers(editor, col.querySelector('#logic-lines') as HTMLElement);
                        this.updatePreview();
                    };
                }
            } else {
                col.innerHTML = `
                    <div class="col-content-padded">
                        <div class="section-tag tag-config">
                            <app-icon name="settings" size="xs"></app-icon> Setup
                        </div>
                        <div class="config-form-container" style="margin-top: 0.5rem;">
                            ${PluginConfigManager.renderForm(this.pendingType, this.currentConfig)}
                        </div>
                    </div>
                `;
                const container = col.querySelector('.config-form-container') as HTMLElement;
                if (container) {
                    PluginConfigManager.attachEvents(container, this.pendingType, (cfg) => {
                        this.currentConfig = JSON.stringify(cfg);
                    });
                }
            }
        } else if (index === 2) {
            // Column 2: Simulation & Preview
            col.innerHTML = `
                <div class="col-content-padded" style="flex: 1; display: flex; flex-direction: column;">
                    <div class="section-tag tag-preview">
                        <app-icon name="play" size="xs"></app-icon> Live Preview
                    </div>

                    ${this.isMockingMode ? `
                    <div class="preview-header" style="margin-top: 0.5rem;">
                        <div class="preview-tabs">
                            <button class="preview-tab ${this.activeTab === 'preview' ? 'active' : ''}" data-tab="preview">${this.previewLabel}</button>
                            <button class="preview-tab ${this.activeTab === 'response' ? 'active' : ''}" data-tab="response">Simulation Result</button>
                        </div>
                    </div>
                    ` : `
                    <div class="preview-header-simple" style="margin-top: 0.5rem; color: var(--text-dim); font-size: 0.7rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.05em;">
                        ${this.previewLabel}
                    </div>
                    `}

                    <div class="editor-container" style="flex: 1; margin-top: 0.5rem;">
                        <div class="line-numbers" id="output-lines"></div>
                        <div class="editor-content" id="output-viewer"></div>
                    </div>

                    ${this.simResult ? `
                        <div class="sim-meta-footer" style="margin-top: 0.5rem; padding: 0.5rem; background: rgba(0,0,0,0.2); border-radius: 4px; font-size: 0.8rem;">
                            <div style="display: flex; justify-content: space-between; margin-bottom: 0.25rem;">
                                <span style="color: var(--text-dim);">Status:</span>
                                <span style="color: ${(this.simResult.response?.status || 0) < 400 ? '#4caf50' : '#f44336'}">${this.simResult.response?.status || 'N/A'}</span>
                            </div>
                            <div style="display: flex; justify-content: space-between;">
                                <span style="color: var(--text-dim);">Duration:</span>
                                <span>${this.simResult.duration_ms}ms</span>
                            </div>
                        </div>
                    ` : ''}
                </div>
            `;
            
            const viewer = col.querySelector('#output-viewer') as HTMLElement;
            if (viewer) {
                let contentHtml = '';
                if (this.activeTab === 'preview') {
                    contentHtml = this.transformPreview || '// No transformation results yet...';
                } else if (this.activeTab === 'response') {
                    if (this.simLoading) {
                        contentHtml = '// Executing simulation...';
                    } else if (this.simError) {
                        contentHtml = `// Error: ${this.simError}`;
                    } else if (this.simResult) {
                        contentHtml = JSON.stringify(this.simResult.response || {}, null, 2);
                    } else {
                        contentHtml = '// Run simulation to see response';
                    }
                }
                viewer.innerText = contentHtml;
                this.updateLineNumbers(viewer, col.querySelector('#output-lines') as HTMLElement);
            }

            col.querySelectorAll('.preview-tab').forEach(tab => {
                tab.addEventListener('click', () => {
                    this.activeTab = tab.getAttribute('data-tab') as 'preview' | 'response';
                    this.renderColumn(2);
                });
            });


        }
    }

    private parseConfig(content: string): any {
        if (!content) return {};
        try {
            // @ts-ignore
            return yaml.load(content) || {};
        } catch (e) {
            try {
                return JSON.parse(content) || {};
            } catch (e2) {
                return {};
            }
        }
    }

    private updateGridLayout() {
        const grid = this.shadowRoot?.querySelector('.mapping-grid') as HTMLElement;
        if (!grid) return;

        const col0 = grid.querySelector('.mapping-col[data-index="0"]');
        const res0 = grid.querySelector('.resizer[data-index="0"]');
        const col1 = grid.querySelector('.mapping-col[data-index="1"]');
        const res1 = grid.querySelector('.resizer[data-index="1"]');
        const col2 = grid.querySelector('.mapping-col[data-index="2"]');

        // Always show all 3 columns as requested by the user for persistent preview
        grid.style.gridTemplateColumns = `${this.colWeights[0]}fr 4px ${this.colWeights[1]}fr 4px ${this.colWeights[2]}fr`;
        [col0, res0, col1, res1, col2].forEach(el => el?.classList.remove('hidden'));
    }

    private syncSampleSourceFromForm() {
        try {
            const source = JSON.parse(this.sampleSource);
            
            // Get current domain hostname
            const domain = this.allDomains.find(d => d.id === this.selectedDomainId);
            const host = domain ? domain.host : 'api.example.com';
            
            // Update Method
            source.method = this.sampleSourceMethod;
            
            // Update URL Path
            if (source.url) {
                try {
                    const url = new URL(source.url);
                    url.hostname = host;
                    url.pathname = this.sampleSourcePath;
                    source.url = url.toString();
                } catch (e) {
                    // Fallback if URL is not absolute
                    source.url = `https://${host}${this.sampleSourcePath.startsWith('/') ? '' : '/'}${this.sampleSourcePath}`;
                }
            } else {
                source.url = `https://${host}${this.sampleSourcePath.startsWith('/') ? '' : '/'}${this.sampleSourcePath}`;
            }

            this.sampleSource = JSON.stringify(source, null, 2);
            this.renderColumn(0);
            this.updatePreview();
        } catch (e) {
            console.warn('Failed to sync sample source from form - likely invalid JSON in editor', e);
        }
    }

    private updateLineNumbers(editor: HTMLElement, lineContainer: HTMLElement) {
        if (!editor || !lineContainer) return;
        const lines = (editor.innerText || "").split('\n').length;
        lineContainer.innerHTML = Array.from({ length: lines }, (_, i) => `<div>${i + 1}</div>`).join('');
    }

    render() {
        if (!this.shadowRoot) return;

        this.shadowRoot.innerHTML = `
            ${getPluginEditorStyles(this.seed, this.colWeights)}

            <div class="editor-header">
                <div class="header-title">
                    <app-button variant="secondary" size="sm" icon="arrow-left" id="btnClose"></app-button>
                    <h2>${this._pluginId ? 'Edit Plugin' : 'Create Plugin'}</h2>
                </div>
                <div class="header-actions" style="display: flex; align-items: center; gap: 1rem;">
                    <div style="display: flex; align-items: center; gap: 0.5rem; margin-right: 0.5rem;">
                        <label style="font-size: 0.8rem; color: var(--text-dim);">Active</label>
                        <form-toggle id="toggle-active" ${this.pendingActive ? 'checked' : ''}></form-toggle>
                    </div>
                    <app-button variant="primary" id="btnSave" icon="save" data-tooltip="Save Changes" ${this.isSaving ? 'loading' : ''}></app-button>
                </div>
            </div>

            <div class="editor-body">
                ${this.isLoading ? `
                    <div class="loader-container">
                        <div class="loader"></div>
                        <div style="font-family: var(--font-sketch); color: var(--text-dim); margin-top: 1rem;">Preparing your workshop...</div>
                    </div>
                ` : `
                    <div class="config-bar-container">
                        <div class="config-bar">
                            <form-input id="input-name" label="Name" placeholder="e.g. auth-validator" style="flex: 2;" value="${this.pendingName}"></form-input>
                            <form-select id="select-type" label="Type" style="flex: 1.5;" 
                                options='[
                                    {"value":"transform","label":"Transformation"},
                                    {"value":"api_key_validation","label":"API Key"},
                                    {"value":"cors","label":"CORS"},
                                    {"value":"ip_filter","label":"IP Filter"},
                                    {"value":"payload_size_filter","label":"Payload Limit"},
                                    {"value":"mock","label":"Mocking"}
                                ]'
                                value="${this.pendingType}"></form-select>
                            <form-select id="select-side" label="Position" style="flex: 1;"
                                options='[
                                    {"value":"inbound","label":"Inbound"},
                                    {"value":"outbound","label":"Outbound"}
                                ]'
                                value="${this.pendingSide}"></form-select>
                            <div class="field-row" style="flex: 0.8;">
                                <label>Mocking Mode</label>
                                <div class="config-toggle-row">
                                    <form-toggle id="toggle-mock-mode" ${this.isMockingMode ? 'checked' : ''}></form-toggle>
                                </div>
                            </div>
                        </div>

                        ${this.isMockingMode ? `
                            <div class="mock-drawer" style="padding: 0.5rem 1rem; background: rgba(255,255,255,0.02); display: flex; align-items: flex-end; gap: 1rem; border-top: 1px solid rgba(255,255,255,0.05); animation: slideDown 0.2s ease-out;">
                                <div class="control-group" style="flex: 1;">
                                    <label style="display: block; font-size: 0.7rem; color: var(--text-dim); margin-bottom: 0.25rem;">Source Type</label>
                                    <select id="source-type-select" class="form-input-compact">
                                        <option value="custom" ${this.sourceType === 'custom' ? 'selected' : ''}>Custom Payload</option>
                                        <option value="domain" ${this.sourceType === 'domain' ? 'selected' : ''}>From Domain/Rule</option>
                                    </select>
                                </div>
                                <div class="control-group" style="flex: 1.5;">
                                    <label style="display: block; font-size: 0.7rem; color: var(--text-dim); margin-bottom: 0.25rem;">Domain</label>
                                    <select id="source-domain-select" class="form-input-compact">
                                        <option value="">Select Domain...</option>
                                        ${this.allDomains.map(d => `<option value="${d.id}" ${this.selectedDomainId === d.id ? 'selected' : ''}>${d.host}</option>`).join('')}
                                    </select>
                                </div>
                                <div class="control-group" style="flex: 1.5;">
                                    <label style="display: block; font-size: 0.7rem; color: var(--text-dim); margin-bottom: 0.25rem;">Rule</label>
                                    <select id="source-rule-select" class="form-input-compact">
                                        <option value="">Select Rule...</option>
                                        ${this.sourceRules.map(r => `<option value="${r.id}" ${this.selectedRuleId === r.id ? 'selected' : ''}>${r.name}</option>`).join('')}
                                    </select>
                                </div>
                                <div class="control-group" style="flex: 0.8;">
                                    <label style="display: block; font-size: 0.7rem; color: var(--text-dim); margin-bottom: 0.25rem;">Method</label>
                                    <select id="mock-method" class="form-input-compact">
                                        <option value="GET" ${this.sampleSourceMethod === 'GET' ? 'selected' : ''}>GET</option>
                                        <option value="POST" ${this.sampleSourceMethod === 'POST' ? 'selected' : ''}>POST</option>
                                        <option value="PUT" ${this.sampleSourceMethod === 'PUT' ? 'selected' : ''}>PUT</option>
                                        <option value="DELETE" ${this.sampleSourceMethod === 'DELETE' ? 'selected' : ''}>DELETE</option>
                                    </select>
                                </div>
                                 <div class="control-group" style="flex: 1.5; min-width: 140px;">
                                     <label style="display: block; font-size: 0.7rem; color: var(--text-dim); margin-bottom: 0.25rem;">Path</label>
                                     <div style="display: flex; gap: 0.5rem; align-items: center; width: 100%;">
                                         <input type="text" id="mock-path" class="form-input-compact" placeholder="/v1/resource" value="${this.sampleSourcePath}" style="flex: 1; min-width: 0;">
                                         <app-button variant="success" size="xs" icon="play" id="runSimBtnV4" ${this.simLoading ? 'loading' : ''} style="flex-shrink: 0;"></app-button>
                                     </div>
                                 </div>
                            </div>
                        ` : ''}
                    </div>

                    <div class="mapping-grid">
                        <div class="mapping-col" data-index="0"></div>
                        <div class="resizer" id="resizer0" data-index="0"></div>
                        <div class="mapping-col" data-index="1"></div>
                        <div class="resizer" id="resizer1" data-index="1"></div>
                        <div class="mapping-col" data-index="2"></div>
                    </div>
                `}
            </div>
        `;

        if (!this.isLoading) {
            this.renderColumn(0);
            this.renderColumn(1);
            this.renderColumn(2);
            this.transformPreview = '';
            this.updateColumnWidths();
            this.bindUIEvents();
        }
    }

    private bindUIEvents() {
        this.shadowRoot?.getElementById('btnClose')?.addEventListener('click', () => this.handleClose());
        this.shadowRoot?.getElementById('btnSave')?.addEventListener('click', () => this.handleSave());

        const nameInput = this.shadowRoot?.getElementById('input-name') as HTMLInputElement;
        nameInput?.addEventListener('input', () => this.pendingName = nameInput.value);

        const typeSelect = this.shadowRoot?.getElementById('select-type') as HTMLElement & { value: string };
        typeSelect?.addEventListener('change', () => {
            this.pendingType = typeSelect.value;
            this.sourceType = this.getDefaultSourceType(this.pendingType);
            this.sampleSource = this.getDefaultSampleSource(this.pendingType);
            const isTransform = ['transform', 'request_transform', 'response_transform'].includes(this.pendingType);
            this.activeTab = isTransform ? 'preview' : 'response';
            this.render();
        });

        const sideSelect = this.shadowRoot?.getElementById('select-side') as HTMLElement & { value: string };
        sideSelect?.addEventListener('change', () => {
            this.pendingSide = sideSelect.value as 'inbound' | 'outbound';
            this.render();
        });

        const activeToggle = this.shadowRoot?.getElementById('toggle-active') as HTMLElement & { checked: boolean };
        activeToggle?.addEventListener('change', () => this.pendingActive = activeToggle.checked);

        const mockToggle = this.shadowRoot?.getElementById('toggle-mock-mode') as HTMLElement & { checked: boolean };
        mockToggle?.addEventListener('change', () => {
            this.isMockingMode = mockToggle.checked;
            if (!this.isMockingMode) {
                this.activeTab = 'preview';
            }
            this.render();
        });

        this.shadowRoot?.querySelectorAll('.resizer').forEach((r, i) => {
            r.addEventListener('mousedown', (e) => {
                this.isResizing = true;
                this.resizeIndex = i;
                document.body.style.cursor = 'col-resize';
                e.preventDefault();
            });
        });

        // Drawer Events (Mocking Mode)
        if (this.isMockingMode) {
            this.shadowRoot?.getElementById('source-type-select')?.addEventListener('change', (e) => {
                this.sourceType = (e.target as HTMLSelectElement).value as 'custom' | 'domain';
                this.render();
            });

            this.shadowRoot?.getElementById('source-domain-select')?.addEventListener('change', (e) => {
                this.selectedDomainId = (e.target as HTMLSelectElement).value;
                this.selectedRuleId = '';
                this.sourceRules = this.allRules.filter(r => r.domain_id === this.selectedDomainId);
                this.syncSampleSourceFromForm();
                this.render();
            });

            this.shadowRoot?.getElementById('source-rule-select')?.addEventListener('change', (e) => {
                const ruleId = (e.target as HTMLSelectElement).value;
                this.selectedRuleId = ruleId;
                const rule = this.sourceRules.find(r => r.id === ruleId);
                if (rule) {
                    this.sampleSourceMethod = rule.methods?.[0] || 'GET';
                    this.sampleSourcePath = rule.path || '/';
                    
                    // Force source type to domain if a rule is selected
                    this.sourceType = 'domain';
                    
                    this.syncSampleSourceFromForm();
                    this.render(); 
                    this.updatePreview();
                }
            });

            this.shadowRoot?.getElementById('mock-method')?.addEventListener('change', (e) => {
                this.sampleSourceMethod = (e.target as HTMLSelectElement).value;
                this.syncSampleSourceFromForm();
            });

            this.shadowRoot?.getElementById('mock-path')?.addEventListener('input', (e) => {
                this.sampleSourcePath = (e.target as HTMLInputElement).value;
                this.syncSampleSourceFromForm();
            });

            this.shadowRoot?.getElementById('runSimBtnV4')?.addEventListener('click', () => this.handleRunSimulation());
        }
    }
}

if (!customElements.get('gateway-plugin-editor')) {
    customElements.define('gateway-plugin-editor', PluginEditor);
}
